/**
 * Menu counters for the Admin (D22 NAV-COUNTS, D560) — which menu leaf shows which server queue count, and how the answer of
 * `GET /v1/ops/counts` becomes badges.
 *
 * Rule since D358: a badge appears ONLY from a real, scoped server number. So a leaf whose queue the server did not count (the
 * operator may not list it, it failed to read, the request is still in flight) gets NO badge — and a queue with nothing waiting
 * gets none either (a badge is "something waits"). Several queues on one leaf add up; a group shows the number of items waiting
 * inside it, each counted ONCE, so a collapsed group still says that something inside waits.
 *
 * A number can be a lower bound (`atLeast`): the queue is capped at 100 (drawn `99+`) or the read saw only a full window of its
 * list (drawn `3+`). A sum with a lower bound is a lower bound.
 */
import { type OpsCountKey, type OpsCountsView } from '@vello/api-client';

export type NavBadgeTone = 'danger' | 'warning';

export interface NavCountBadge {
  readonly count: number;
  readonly tone: NavBadgeTone;
  /** The count is a lower bound: drawn `3+`. */
  readonly atLeast: boolean;
}

/** One queue's number as the console keeps it. */
export interface NavCount {
  readonly count: number;
  readonly atLeast: boolean;
}
export type NavCounts = Readonly<Partial<Record<OpsCountKey, NavCount>>>;

/** The queues each menu leaf (by node id) summarizes — the leaf whose screen lists that queue. */
export const NAV_COUNT_SOURCES: Readonly<Record<string, { readonly keys: readonly OpsCountKey[]; readonly tone: NavBadgeTone }>> = {
  courier_jobs: { keys: ['courier_stuck'], tone: 'danger' },
  safety: { keys: ['safety_incidents', 'risk_cases'], tone: 'danger' },
  ops_incidents: { keys: ['ops_incidents'], tone: 'danger' },
  disputes: { keys: ['disputes_open'], tone: 'warning' },
  alert_center: { keys: ['platform_alerts'], tone: 'warning' },
  payouts: { keys: ['payouts_pending'], tone: 'warning' },
  partner_billing: { keys: ['partner_payments'], tone: 'warning' },
  ledger: { keys: ['compensation_requests'], tone: 'warning' },
  driver_documents_all: { keys: ['driver_documents_pending'], tone: 'warning' },
};

/**
 * Queues that are a SUBSET of another: the OPS queue holds the open incidents drivers routed to operations (an SOS, a trip
 * report), and the safety queue lists EVERY open incident — the same rows included (`openQueue` has no queue filter). Each leaf
 * keeps its own list's number; a group containing both counts the inner ones once (they are already in the outer).
 */
export const NAV_COUNT_SUBSETS: readonly { readonly inner: OpsCountKey; readonly outer: OpsCountKey }[] = [{ inner: 'ops_incidents', outer: 'safety_incidents' }];

/** The numbers of an answer: only the queues the server counted; `99+` and a full-window read are lower bounds. */
export function countsFromView(view: OpsCountsView): NavCounts {
  const out: Partial<Record<OpsCountKey, NavCount>> = {};
  for (const [key, reading] of Object.entries(view.counts) as [OpsCountKey, { count: number; capped: boolean; partial?: boolean } | undefined][]) {
    if (reading === undefined) continue;
    out[key] = reading.capped ? { count: Math.max(reading.count, 100), atLeast: true } : { count: reading.count, atLeast: reading.partial === true };
  }
  return out;
}

/**
 * The sum of several scopes' numbers (a STATE is read market by market - ADR-0124 - because the counts route has no region filter and
 * a country-wide read would be the whole country). A queue counted in at least one part is counted; a part that did not answer
 * (`incomplete`) makes every sum a lower bound, never an exact number.
 */
export function sumNavCounts(parts: readonly NavCounts[], incomplete = false): NavCounts {
  const out: Partial<Record<OpsCountKey, NavCount>> = {};
  for (const part of parts) {
    for (const [key, reading] of Object.entries(part) as [OpsCountKey, NavCount | undefined][]) {
      if (reading === undefined) continue;
      const prev = out[key];
      out[key] = { count: (prev?.count ?? 0) + reading.count, atLeast: (prev?.atLeast ?? false) || reading.atLeast || incomplete };
    }
  }
  return out;
}

interface TreeNode {
  readonly id: string;
  readonly children?: readonly TreeNode[];
}

/** The tone of a leaf that holds something, and the keys of the leaves below a node (those the server counted). */
interface Walked {
  readonly keys: ReadonlySet<OpsCountKey>;
  readonly danger: boolean;
  /** What leaves below this node count from a source other than the ops queues (D615: the platform setup). */
  readonly extra: NavCount;
}

const NO_EXTRA: NavCount = { count: 0, atLeast: false };

/**
 * The badge of every node that has one, by node id. A leaf takes the sum of its queues that were counted (none counted or a sum
 * of 0: no badge); a group takes the number of items waiting below it, each counted once, danger when any leaf holding something
 * is.
 *
 * `extra` (D615 SETUP-READINESS) gives a leaf a count that is NOT an ops queue, by leaf id — the platform setup's pending items,
 * always warning tone. A leaf with a count of 0 gets no badge; a group adds what its leaves hold. The ops-queue rules above are
 * unchanged when `extra` is absent.
 */
export function navBadges(tree: readonly TreeNode[], counts: NavCounts | undefined, extra?: Readonly<Record<string, NavCount>>): ReadonlyMap<string, NavCountBadge> {
  const out = new Map<string, NavCountBadge>();
  if (counts === undefined && extra === undefined) return out;
  const total = (keys: ReadonlySet<OpsCountKey>): NavCount => {
    let count = 0;
    let atLeast = false;
    if (counts === undefined) return { count, atLeast };
    for (const key of keys) {
      const c = counts[key];
      if (c === undefined) continue;
      count += c.count;
      atLeast ||= c.atLeast;
    }
    for (const { inner, outer } of NAV_COUNT_SUBSETS) {
      const i = counts[inner];
      const o = counts[outer];
      if (keys.has(inner) && keys.has(outer) && i !== undefined && o !== undefined) count -= Math.min(i.count, o.count);
    }
    return { count, atLeast };
  };
  const walk = (node: TreeNode): Walked => {
    if (node.children !== undefined) {
      const keys = new Set<OpsCountKey>();
      let danger = false;
      let more = NO_EXTRA;
      for (const child of node.children) {
        const w = walk(child);
        for (const k of w.keys) keys.add(k);
        danger ||= w.danger;
        more = { count: more.count + w.extra.count, atLeast: more.atLeast || w.extra.atLeast };
      }
      const t = total(keys);
      const count = t.count + more.count;
      if (count > 0) out.set(node.id, { count, tone: danger ? 'danger' : 'warning', atLeast: t.atLeast || more.atLeast });
      return { keys, danger, extra: more };
    }
    const own = extra?.[node.id];
    if (own !== undefined) {
      if (own.count > 0) out.set(node.id, { count: own.count, tone: 'warning', atLeast: own.atLeast });
      return { keys: new Set(), danger: false, extra: own.count > 0 ? own : NO_EXTRA };
    }
    const source = NAV_COUNT_SOURCES[node.id];
    if (source === undefined || counts === undefined) return { keys: new Set(), danger: false, extra: NO_EXTRA };
    const keys = new Set(source.keys.filter((k) => counts[k] !== undefined));
    if (keys.size === 0) return { keys, danger: false, extra: NO_EXTRA };
    const t = total(keys);
    if (t.count <= 0) return { keys: new Set(), danger: false, extra: NO_EXTRA };
    out.set(node.id, { count: t.count, tone: source.tone, atLeast: t.atLeast });
    return { keys, danger: source.tone === 'danger', extra: NO_EXTRA };
  };
  for (const node of tree) walk(node);
  return out;
}
